/**
 * Logging Actions - Type-safe action creators for all loggable events
 * 
 * Following web logging conventions:
 * - itemId: Most important parameter for grouping/filtering (e.g., bookId, answerId)
 * - params: Only action-related data; server adds user/device info
 * - Static event names with human-readable slugs for onboarding
 */

/**
 * Action types for all loggable events
 */
export enum LogActionType {
  // Navigation
  SCREEN_VIEWED = 'SCREEN_VIEWED',
  SCREEN_FOCUS = 'SCREEN_FOCUS',
  
  // User Actions
  BUTTON_PRESSED = 'BUTTON_PRESSED',
  FORM_SUBMITTED = 'FORM_SUBMITTED',
  INPUT_CHANGED = 'INPUT_CHANGED',
  
  // Chat
  CHAT_MESSAGE_SENT = 'CHAT_MESSAGE_SENT',
  CHAT_MESSAGE_RECEIVED = 'CHAT_MESSAGE_RECEIVED',
  QUICK_ACTION_PRESSED = 'QUICK_ACTION_PRESSED',
  
  // Content
  BOOK_OPENED = 'BOOK_OPENED',
  CHAPTER_OPENED = 'CHAPTER_OPENED',
  BOOK_ADDED_TO_FAVORITES = 'BOOK_ADDED_TO_FAVORITES',
  BOOK_REMOVED_FROM_FAVORITES = 'BOOK_REMOVED_FROM_FAVORITES',
  INSIGHT_OPENED = 'INSIGHT_OPENED',
  INSIGHT_COMPLETED = 'INSIGHT_COMPLETED',
  
  // Onboarding
  ONBOARDING_STARTED = 'ONBOARDING_STARTED',
  ONBOARDING_STEP_COMPLETED = 'ONBOARDING_STEP_COMPLETED',
  ONBOARDING_COMPLETED = 'ONBOARDING_COMPLETED',
  
  // Auth
  USER_SIGNED_UP = 'USER_SIGNED_UP',
  USER_SIGNED_IN = 'USER_SIGNED_IN',
  USER_SIGNED_OUT = 'USER_SIGNED_OUT',
  
  // App Lifecycle
  APP_OPENED = 'APP_OPENED',
  APP_BACKGROUNDED = 'APP_BACKGROUNDED',
  APP_FOREGROUND = 'APP_FOREGROUND',
  
  // External Transfer & Context
  EXTERNAL_TRANSFER_STARTED = 'EXTERNAL_TRANSFER_STARTED',
  EXTERNAL_TRANSFER_COMPLETED = 'EXTERNAL_TRANSFER_COMPLETED',
  USER_CONTEXT = 'USER_CONTEXT',
  
  // Subscription & Purchases
  PAYWALL_VIEWED = 'PAYWALL_VIEWED',
  PAYWALL_STEP_CHANGED = 'PAYWALL_STEP_CHANGED',
  PAYWALL_DISMISSED = 'PAYWALL_DISMISSED',
  PURCHASE_STARTED = 'PURCHASE_STARTED',
  PURCHASE_COMPLETED = 'PURCHASE_COMPLETED',
  PURCHASE_FAILED = 'PURCHASE_FAILED',
  PURCHASE_CANCELLED = 'PURCHASE_CANCELLED',
  PACKAGE_SELECTED = 'PACKAGE_SELECTED',
  PURCHASES_RESTORED = 'PURCHASES_RESTORED',
  RESTORE_PURCHASES_STARTED = 'RESTORE_PURCHASES_STARTED',
  RESTORE_PURCHASES_FAILED = 'RESTORE_PURCHASES_FAILED',
  
  // Errors
  ERROR_OCCURRED = 'ERROR_OCCURRED',
}

/**
 * Log action interface
 */
export interface LogAction {
  type: LogActionType;
  timestamp: number;
  itemId?: string | null;  // Most important parameter for grouping/filtering
  params?: Record<string, any>;  // Only action-related data
}

// ============================================
// Action Creators
// ============================================

/**
 * Navigation Actions
 */
export const screenViewed = (screenName: string, valueId?: string | null): LogAction => ({
  type: LogActionType.SCREEN_VIEWED,
  timestamp: Date.now(),
  itemId: valueId ?? null,  // Value should be empty or an ID relevant to the screen
  params: { screenName },   // Keep screen name in params for analytics mapping
});

/**
 * Content Actions
 */
export const bookOpened = (bookId: string, bookTitle?: string, source?: string): LogAction => ({
  type: LogActionType.BOOK_OPENED,
  timestamp: Date.now(),
  itemId: bookId,  // ID goes to itemId for grouping
  params: {
    bookTitle,  // Optional reference name
    source,
  },
});

export const chapterOpened = (
  bookId: string, 
  chapterId: string, 
  chapterTitle?: string
): LogAction => ({
  type: LogActionType.CHAPTER_OPENED,
  timestamp: Date.now(),
  itemId: chapterId,  // Chapter ID for grouping
  params: {
    bookId,
    chapterTitle,
  },
});

export const bookAddedToFavorites = (bookId: string, bookTitle?: string): LogAction => ({
  type: LogActionType.BOOK_ADDED_TO_FAVORITES,
  timestamp: Date.now(),
  itemId: bookId,
  params: {
    bookTitle,
  },
});

export const bookRemovedFromFavorites = (bookId: string, bookTitle?: string): LogAction => ({
  type: LogActionType.BOOK_REMOVED_FROM_FAVORITES,
  timestamp: Date.now(),
  itemId: bookId,
  params: {
    bookTitle,
  },
});

export const insightOpened = (
  insightId: string, 
  bookId?: string,
  insightTitle?: string
): LogAction => ({
  type: LogActionType.INSIGHT_OPENED,
  timestamp: Date.now(),
  itemId: insightId,
  params: {
    bookId,
    insightTitle,
  },
});

export const insightCompleted = (
  insightId: string, 
  bookId?: string,
  duration?: number
): LogAction => ({
  type: LogActionType.INSIGHT_COMPLETED,
  timestamp: Date.now(),
  itemId: insightId,
  params: {
    bookId,
    duration,
  },
});

/**
 * Chat Actions
 */
export const chatMessageSent = (
  messageId: string,
  mentorId: string,
  messageLength: number
): LogAction => ({
  type: LogActionType.CHAT_MESSAGE_SENT,
  timestamp: Date.now(),
  itemId: mentorId,
  params: {
    messageId,
    messageLength,
  },
});

export const chatMessageReceived = (
  messageId: string,
  mentorId: string,
  hasRecommendations: boolean
): LogAction => ({
  type: LogActionType.CHAT_MESSAGE_RECEIVED,
  timestamp: Date.now(),
  itemId: mentorId,  // Mentor ID for grouping
  params: {
    messageId,
    hasRecommendations,
  },
});

export const quickActionPressed = (
  actionId: string,
  mentorId: string
): LogAction => ({
  type: LogActionType.QUICK_ACTION_PRESSED,
  timestamp: Date.now(),
  itemId: actionId,
  params: {
    mentorId,
  },
});

/**
 * Onboarding Actions
 */
export const onboardingStarted = (onboardingType: string): LogAction => ({
  type: LogActionType.ONBOARDING_STARTED,
  timestamp: Date.now(),
  itemId: onboardingType,
  params: {},
});

export const onboardingQuestionAnswered = (
  questionSlug: string,
  answer: string | string[]
): LogAction => ({
  type: LogActionType.ONBOARDING_STEP_COMPLETED,
  timestamp: Date.now(),
  itemId: Array.isArray(answer) ? answer.join(',') : answer,  // Answer(s) for grouping
  params: {
    questionSlug,  // Human-readable question ID
  },
});

export const onboardingCompleted = (
  onboardingType: string,
  duration?: number,
  totalSteps?: number
): LogAction => ({
  type: LogActionType.ONBOARDING_COMPLETED,
  timestamp: Date.now(),
  itemId: onboardingType,
  params: {
    duration,
    totalSteps,
  },
});

/**
 * User Action Tracking
 */
export const buttonPressed = (
  buttonId: string, 
  context?: Record<string, any>
): LogAction => ({
  type: LogActionType.BUTTON_PRESSED,
  timestamp: Date.now(),
  itemId: buttonId,
  params: context,
});

export const formSubmitted = (
  formId: string,
  fields?: string[]
): LogAction => ({
  type: LogActionType.FORM_SUBMITTED,
  timestamp: Date.now(),
  itemId: formId,
  params: {
    fields,
  },
});

/**
 * Auth Actions
 */
export const userSignedUp = (method: string): LogAction => ({
  type: LogActionType.USER_SIGNED_UP,
  timestamp: Date.now(),
  itemId: method,
  params: {},
});

export const userSignedIn = (method: string): LogAction => ({
  type: LogActionType.USER_SIGNED_IN,
  timestamp: Date.now(),
  itemId: method,
  params: {},
});

export const userSignedOut = (): LogAction => ({
  type: LogActionType.USER_SIGNED_OUT,
  timestamp: Date.now(),
  itemId: null,
  params: {},
});

/**
 * App Lifecycle Actions
 */
export const appOpened = (): LogAction => ({
  type: LogActionType.APP_OPENED,
  timestamp: Date.now(),
  itemId: null,
  params: {},
});

export const appBackgrounded = (duration?: number): LogAction => ({
  type: LogActionType.APP_BACKGROUNDED,
  timestamp: Date.now(),
  itemId: null,
  params: {
    duration,
  },
});

export const appForegrounded = (backgroundDuration?: number): LogAction => ({
  type: LogActionType.APP_FOREGROUND,
  timestamp: Date.now(),
  itemId: null,
  params: {
    backgroundDuration,
  },
});

/**
 * External transfer events
 */
export const externalTransferStarted = (source?: string): LogAction => ({
  type: LogActionType.EXTERNAL_TRANSFER_STARTED,
  timestamp: Date.now(),
  itemId: source || null,
  params: {},
});

export const externalTransferCompleted = (
  userId: string,
  hasOnboardingData: boolean,
  hasStructuredProfile: boolean
): LogAction => ({
  type: LogActionType.EXTERNAL_TRANSFER_COMPLETED,
  timestamp: Date.now(),
  itemId: userId,
  params: {
    hasOnboardingData,
    hasStructuredProfile,
  },
});

/**
 * User context snapshot (safe, non-PII summary)
 */
export const userContext = (
  context: Record<string, any>
): LogAction => ({
  type: LogActionType.USER_CONTEXT,
  timestamp: Date.now(),
  itemId: null,
  params: context,
});

/**
 * Subscription & Purchase Actions
 */
export const paywallViewed = (source?: string): LogAction => ({
  type: LogActionType.PAYWALL_VIEWED,
  timestamp: Date.now(),
  itemId: source || null,
  params: {},
});

export const paywallStepChanged = (
  fromStep: string,
  toStep: string
): LogAction => ({
  type: LogActionType.PAYWALL_STEP_CHANGED,
  timestamp: Date.now(),
  itemId: toStep,
  params: {
    fromStep,
  },
});

export const paywallDismissed = (
  currentStep: string,
  timeSpent?: number
): LogAction => ({
  type: LogActionType.PAYWALL_DISMISSED,
  timestamp: Date.now(),
  itemId: currentStep,
  params: {
    timeSpent,
  },
});

export const packageSelected = (
  packageId: string,
  tier: string,
  period: string,
  price?: string
): LogAction => ({
  type: LogActionType.PACKAGE_SELECTED,
  timestamp: Date.now(),
  itemId: packageId,
  params: {
    tier,
    period,
    price,
  },
});

export const purchaseStarted = (
  packageId: string,
  tier: string,
  period: string,
  price?: string
): LogAction => ({
  type: LogActionType.PURCHASE_STARTED,
  timestamp: Date.now(),
  itemId: packageId,
  params: {
    tier,
    period,
    price,
  },
});

export const purchaseCompleted = (
  packageId: string,
  tier: string,
  period: string,
  price?: string
): LogAction => ({
  type: LogActionType.PURCHASE_COMPLETED,
  timestamp: Date.now(),
  itemId: packageId,
  params: {
    tier,
    period,
    price,
  },
});

export const purchaseFailed = (
  packageId: string,
  errorMessage: string,
  tier?: string,
  period?: string
): LogAction => ({
  type: LogActionType.PURCHASE_FAILED,
  timestamp: Date.now(),
  itemId: packageId,
  params: {
    errorMessage,
    tier,
    period,
  },
});

export const purchaseCancelled = (
  packageId: string,
  tier?: string,
  period?: string
): LogAction => ({
  type: LogActionType.PURCHASE_CANCELLED,
  timestamp: Date.now(),
  itemId: packageId,
  params: {
    tier,
    period,
  },
});

export const restorePurchasesStarted = (
  trigger: 'manual' | 'automatic'
): LogAction => ({
  type: LogActionType.RESTORE_PURCHASES_STARTED,
  timestamp: Date.now(),
  itemId: trigger,
  params: {},
});

export const purchasesRestored = (
  success: boolean,
  trigger: 'manual' | 'automatic',
  details?: {
    tier?: string;
    expiresAt?: number | null;
    isLifetime?: boolean;
    willRenew?: boolean;
    source?: string;
    customerId?: string;
    entitlements?: string[];
    errorMessage?: string;
  }
): LogAction => ({
  type: LogActionType.PURCHASES_RESTORED,
  timestamp: Date.now(),
  itemId: trigger,
  params: {
    success,
    tier: details?.tier,
    expiresAt: details?.expiresAt,
    isLifetime: details?.isLifetime,
    willRenew: details?.willRenew,
    source: details?.source,
    customerId: details?.customerId,
    entitlements: details?.entitlements,
    errorMessage: details?.errorMessage,
  },
});

export const restorePurchasesFailed = (
  trigger: 'manual' | 'automatic',
  errorMessage: string
): LogAction => ({
  type: LogActionType.RESTORE_PURCHASES_FAILED,
  timestamp: Date.now(),
  itemId: trigger,
  params: {
    errorMessage,
  },
});

/**
 * Error Actions
 */
export const errorOccurred = (
  errorMessage: string,
  errorType: string,
  context?: Record<string, any>
): LogAction => ({
  type: LogActionType.ERROR_OCCURRED,
  timestamp: Date.now(),
  itemId: errorType,
  params: {
    errorMessage,
    ...context,
  },
});

